import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/customer-ui/components/input-group";
import { Separator } from "@babackapp/customer-ui/components/separator";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import { EnterIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, ArrowLeft } from "lucide-react";
import { motion } from "motion/react";
import { Controller, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAddressForm } from "../hooks/use-address-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { CountryCombobox } from "@babackapp/return-widget-components/components/country-combobox";
import { ProvinceSelect } from "@babackapp/return-widget-components/components/province-select";
import { provincesOf } from "@babackapp/return-widget-components/utils/provinces";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";

export function ReturnWidgetAddressCheck() {
  const { form, onSubmit, userPortalTexts } = useAddressForm();
  const t = useCustomText();
  const countryCode = useWatch({ control: form.control, name: "country_code" });
  const provinces = provincesOf(countryCode);

  const errors = form.formState.isSubmitted
    ? Object.values(form.formState.errors).flatMap((e) =>
        e?.message ? [t(e.message)] : [],
      )
    : [];

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("addressCheck.title")}</HeaderTitle>
        <HeaderDescription>{t("addressCheck.description")}</HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            <ul className="list-disc pl-4">
              {errors.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-6 gap-2"
        >
          <Controller
            control={form.control}
            name="companyName"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-6">
                <InputGroupInput
                  placeholder={t("common.fields.companyName")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>
                    {t("common.fields.companyName")}
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.firstName")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>
                    {t("common.fields.firstName")}
                  </InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.lastNameShort")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.lastName")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="address1"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address1")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address1")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="address2"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.address2Placeholder")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.address2")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="city"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.city")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.city")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="zip"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-3 col-span-2">
                <InputGroupInput
                  placeholder={t("common.fields.zip")}
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.zip")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          {/*
            A picker, not a text input: this field feeds `shipping_address.country_code`,
            which carrier compatibility matches by strict equality against ISO 3166-1
            alpha-2 lists. A hand-typed "France" resolved no carrier and left the return
            without a label — or blocked it at approval on shops without
            `canReturnWithoutLabel`.
          */}
          <Controller
            control={form.control}
            name="country_code"
            render={({ field }) => (
              <div className="col-start-5 col-span-2 flex flex-col gap-1">
                {/* Matches the package's own country cell in gift-address.page.tsx,
                    not the InputGroupAddon labels of the sibling text fields. */}
                <span className="text-xs text-muted-foreground px-0.5">
                  {t("common.fields.country")}
                </span>
                <CountryCombobox
                  value={field.value}
                  onChange={(iso) => {
                    field.onChange(iso);
                    // A state belongs to one country: keep it only if the new
                    // country still lists it.
                    const current = form.getValues("province_code");
                    if (!provincesOf(iso)?.some((p) => p.code === current)) {
                      form.setValue("province_code", "");
                    }
                  }}
                  placeholder={t("common.fields.country")}
                />
              </div>
            )}
          />
          {provinces && (
            <Controller
              control={form.control}
              name="province_code"
              render={({ field, fieldState }) => (
                <div className="col-start-1 col-span-3 flex flex-col gap-1">
                  <span className="text-xs text-muted-foreground px-0.5">
                    {t("common.fields.province")}
                  </span>
                  <ProvinceSelect
                    provinces={provinces}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("common.fields.province")}
                    isInvalid={!!fieldState.error}
                  />
                </div>
              )}
            />
          )}

          <Separator className="col-span-6 my-4" />

          <Controller
            control={form.control}
            name="email"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-1 col-span-3">
                <InputGroupInput
                  placeholder={t("common.fields.email")}
                  type="email"
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.email")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <InputGroup className="h-auto col-start-4 col-span-3">
                <InputGroupInput
                  placeholder="+33 6 00 00 00 00"
                  type="tel"
                  {...field}
                />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{t("common.fields.phone")}</InputGroupText>
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Link to="../return-mode">
              <Button variant="secondary" type="button">
                <ArrowLeft />
                {t("common.back")}
              </Button>
            </Link>
          </AnimatedButtonContainer>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit">
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
